<template>
  <div>
    <v-card
      class="calendar ma-auto elevation-3"
      :class="clientView ? 'form-view' : 'mt-2 mb-10'"
    >
      <v-sheet
        tile
        height="56"
        class="d-flex justify-space-between align-center"
      >
        <div class="control">
          <div class="control__left">
            <div class="control__left--month">
              <v-btn icon class="ma-2" @click="$refs.calendar.prev()">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <p class="current-page">{{ currentPage }}</p>
              <v-btn icon class="ma-2 control-icon" @click="$refs.calendar.next()">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
            <div class="control__left--workload">
              <label for="workload">Workload Type</label>
              <select
                v-model="workload"
                class="control__left--workload--select"
              >
                <option 
                  v-for="option in workloadOptionsList"
                  :key="option"
                  :value="option">
                  {{ option }}
                </option>
              </select>
            </div>
            <div class="control__left--jumpTo">
              <p class="control__left--jumpTo--text">Jump to:</p>
              <button
                class="control__left--jumpTo--btn"
                @click.prevent="jumpToCurrentMonth"
                >
                This Month
              </button>
            </div>
          </div>
          <div class="control__right">
            <button v-if="isFCStaff"
              class="control__right--btn"
              @click="handleDefaultCapacity">
              Set Default Workload
            </button>
          </div>
        </div>
      </v-sheet>
      <v-sheet height="600">
        <v-skeleton-loader
          v-if="showCalendarLoader"
          type="table-tbody@2"
        ></v-skeleton-loader>
        <v-calendar
          v-if="!showCalendarLoader"
          ref="calendar"
          v-model="value"
          :weekdays="[1, 2, 3, 4, 5, 6, 0]"
          type="month"
          :events="events"
          @change="getCurrentPage($event)"
          @click:date="clientView ? null : markUnavailable($event)"
          @click:event="clientView ? null : editUnavailabilityRecord($event)"
        >
        <template v-slot:day="{ date }">
          <div class="custom-day">
             <v-tooltip bottom color="primary">
              <template v-slot:activator="{ on, attrs }">
              <div class="custom-day__empty" 
                v-bind="attrs" 
                v-on="on" 
                :style="`background-color: ${workload.includes('Copywriting') ? '#EED9FF' : '#D6E6FF'} `"
                @click="markUnavailable({date:date})"
                v-if="getWorkloadDataByDate(date).limit === 0 && getWorkloadDataByDate(date).limitOnDate ||
                  workloadUserData && !workloadUserData[date]">
              </div>
              <div class="custom-day__checkmark" 
                v-bind="attrs" 
                v-on="on" 
                :style="`background-color: ${workload.includes('Copywriting') ? '#C57FFF' : '#0266E3'};`"
                @click="markUnavailable({date:date})"
                v-else-if="getWorkloadDataByDate(date).limit / getWorkloadDataByDate(date).limitOnDate > 0.9 
                  && getWorkloadDataByDate(date).limit / getWorkloadDataByDate(date).limitOnDate < 1.05">
                <v-icon color="white">mdi-check</v-icon>
              </div>
              <div class="custom-day__exclamation" 
                v-bind="attrs"
                v-on="on" 
                @click="markUnavailable({date:date})"
                v-else-if="getWorkloadDataByDate(date).limit / getWorkloadDataByDate(date).limitOnDate >= 1.05">
                !
              </div>
              <div class="custom-day__close"
                v-bind="attrs" 
                v-on="on" 
                @click="markAvailability({date:date})"
                v-else-if="getWorkloadDataByDate(date).limitOnDate === 0">
                <v-icon color="#999999">mdi-close</v-icon>
              </div>
              <div class="custom-day__dot"
                v-else
                v-bind="attrs" 
                v-on="on" 
                :style="`background-color: ${workload.includes('Copywriting') ? '#EED9FF' : '#D6E6FF'} `"
                @click="markUnavailable({date:date})">
                <div
                  class="custom-day__dot--inner"
                  :style="`
                    margin: auto;
                    border-radius: 50%;
                    width: ${Math.max(0, Math.min(1, getWorkloadDataByDate(date)?.limit / getWorkloadDataByDate(date)?.limitOnDate)) * 30 + 10}px;
                    height: ${Math.max(0, Math.min(1, getWorkloadDataByDate(date)?.limit / getWorkloadDataByDate(date)?.limitOnDate)) * 30 + 10}px;
                    background-color: ${workload.includes('Copywriting') ? '#C57FFF' : '#0266E3'} `"
                />
              </div>
              </template>
              <div class="tooltip">
                <p class="tooltip__text mb-0">Available Workload: {{ getWorkloadDataByDate(date).limitOnDate - getWorkloadDataByDate(date).limit }} words</p>
              </div>
            </v-tooltip>
          </div>
        </template>
        </v-calendar>
      </v-sheet>
      <v-row justify="center">
        <v-dialog
          v-model="unavailabilityRecord"
          width="700px"
          @click:outside="handleUnavailabilityRecordClosing"
          @keydown.esc="handleUnavailabilityRecordClosing"
        >
        <div class="default-capacity">
            <h3 class="default-capacity__title mb-5" v-if="editMode">Remove Unavailability Record</h3>
            <h3 class="default-capacity__title mb-5" v-else>Unavailability Record</h3>
            <v-container>
              <v-row class="mb-5">
                <v-col cols="12" :sm="editMode ? 12 : 6">
                  <v-menu
                    v-model="unavailabilityDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="unavailabilityDatesRange"
                        label="Date(s) unavailable"
                        prepend-icon="mdi-calendar-remove"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="
                          unavailabilityDatesValidation
                            ? null
                            : dateValidationError
                        "
                        :rules="tab == 0 ? [rules.required] : []"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="unavailabilityDates"
                      range
                      :rules="tab == 0 ? [rules.required] : []"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6" v-if="!editMode">
                  <v-combobox
                    ref="comboBox"
                    outlined
                    label="Note"
                    :items="suggestedNotes"
                    v-model="note"
                    :rules="tab == 0 ? [rules.required] : []"
                  ></v-combobox>
                </v-col>
              </v-row>
              <v-row class="default-capacity__action d-flex align-center justify-end">
                <v-btn
                  depressed
                  :loading="updatingProcessing"
                  @click="handleUnavailabilityRecordClosing"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="#1976D2"
                  class="mx-3 white--text"
                  :disabled="isUnavailabilitySaveDisabled"
                  :loading="updatingProcessing"
                  @click="handleUnavailabilityRecord"
                  >Save</v-btn
                >
              </v-row>
            </v-container>
          </div>
        </v-dialog>
      </v-row>
      <v-row justify="center">
        <v-dialog 
          v-model="defaultCapacity" 
          width="700">
          <div class="default-capacity">
            <h3 class="default-capacity__title mb-5">Default Daily Workload (Words)</h3>
            <v-container>
              <v-row class="mb-5">
                <v-col cols="12" sm="6" class="pb-0">
                  <v-text-field
                    outlined
                    label="Localization Primary"
                    type="number"
                    hide-details
                    @keypress="handleKeypress($event)"
                    @paste="handlePaste($event)"
                    v-model="localizationPrimary"
                  />
                </v-col>
                <v-col cols="12" sm="6" class="pb-0">
                  <v-text-field
                    outlined
                    label="Localization Secondary"
                    type="number"
                    @keypress="handleKeypress($event)"
                    @paste="handlePaste($event)"
                    hide-details
                    v-model="localizationSecondary"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6" class="pb-0">
                  <v-text-field
                    outlined
                    label="Copywriting Primary"
                    type="number"
                    @keypress="handleKeypress($event)"
                    @paste="handlePaste($event)"
                    hide-details
                    v-model="copywritingPrimary"
                  />
                </v-col>
                <v-col cols="12" sm="6" class="pb-0">
                  <v-text-field
                    outlined
                    label="Copywriting Secondary"
                    type="number"
                    @keypress="handleKeypress($event)"
                    @paste="handlePaste($event)"
                    hide-details
                    v-model="copywritingSecondary"
                  />
                </v-col>
              </v-row>
              <v-row class="mb-5">
                <v-col cols="12" class="pb-0">
                  <v-checkbox v-model="defaultWorkload.workingOnWeekend">
                    <template v-slot:label>
                      <div>
                        <span>Working on Weekends</span>
                      </div>
                    </template>
                  </v-checkbox>
                </v-col>
              </v-row>
              <v-row class="default-capacity__action d-flex align-center justify-end">
                <v-btn
                  depressed
                  :loading="updatingProcessing"
                  @click="defaultCapacity = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="#1976D2"
                  class="mx-3 white--text"
                  :loading="updatingProcessing"
                  @click="saveDefaultCapacity"
                  >Save</v-btn
                >
              </v-row>
            </v-container>
          </div>
        </v-dialog>
      </v-row>
      <v-alert v-model="userNotFound" type="error">
        Your email can't be found in company's database. Please log in with your
        work email or contact Project Manager in case of issues with access.
      </v-alert>
    </v-card>
  </div>
</template>

<script>
// vuex
import { mapGetters, mapActions } from "vuex";
// internal
import {
  processEvent,
  getEvents,
  deleteEvent,
  processUser,
  getAllStaff,
  getUserWorkloadSettings,
  getUserWorkload,
  getMultipleUsersWorkload,
  updateDefaultWorkload
} from "@/utils/newDbUtils";
import { publishMessage } from "@/utils/slackUtils";
import { timeUnits } from "@/utils/mixins";

export default {
  name: "Calendar",
  props: {
    clientView: {
      type: Boolean,
      required: false,
      default: false,
    },
    employee: {
      type: Object,
      required: false,
    },
  },
  data: () => ({
    workloadOptions: [
      {
        title: "Localization | Primary",
        value: "localizationPrimary",
      },
      {
        title: "Localization | Secondary",
        value: "localizationSecondary",
      },
      {
        title: "Copywriting | Primary",
        value: "copywritingPrimary",
      },
      {
        title: "Copywriting | Secondary",
        value: "copywritingSecondary",
      }
    ],
    workload: "Localization | Primary",
    userNotFound: false,
    showCalendarLoader: false,
    value: "",
    workloadUserData: null,
    currentDate: null,
    currentPage: "",
    events: [],
    valid: true,
    unavailabilityRecord: false,
    unavailabilityDateMenu: false,
    unavailabilityDates: [],
    note: null,
    limitedCapacityDateMenu: false,
    limitedCapacityDates: [],
    limitedCapacityNote: null,
    localization: null,
    editing: null,
    tab: 0,
    creationProcessing: false,
    editMode: false,
    recordID: null,
    defaultWorkload: {
      copywritingPrimary: null,
      copywritingSecondary: null,
      localizationPrimary: null,
      localizationSecondary: null,
      workingOnWeekend: false
    },
    updatingProcessing: false,
    deletionProcessing: false,
    defaultCapacity: false,
    defaultCapacityValid: true,
    localizationDefaultCapacity: null,
    editingDefaultCapacity: null,
    confirmationProcessing: false,
    dateValidationError: "End date can't be before start date",
    rules: {
      required: (value) => !!value || "Required",
    },
  }),
  methods: {
    ...mapActions("flashMessage", ["handleFlash"]),
    ...mapActions("auth", ["setUser"]),
    handleKeypress(event) {
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode < 48 || charCode > 57) {
        event.preventDefault();
      }
      
      const currentValue = this.value || '';
      if (currentValue.toString().length >= 5) {
        event.preventDefault();
      }
    },
    handlePaste(event) {
      event.preventDefault();
      const pastedText = (event.clipboardData || window.clipboardData).getData('text');
      const sanitized = pastedText.replace(/\D/g, '').slice(0, 5);
      this.value = sanitized;
    },
    jumpToCurrentMonth() {
      this.value = new Date().toISOString().substr(0, 10);
    },
    getWorkloadDataByDate(date) {
      if (this.workload === "Localization | Primary") {
        let limit = this.workloadUserData && this.workloadUserData[date]?.localizationPrimary || 0;
        let limitOnDate = this.workloadUserData && this.workloadUserData[date]?.localizationPrimaryLimit || 0;
        return { limit, limitOnDate };
      } else if (this.workload === "Localization | Secondary") {
        let limit = this.workloadUserData && this.workloadUserData[date]?.localizationSecondary || this.workloadUserData[date]?.localizationSecondary === 0
          ? this.workloadUserData[date].localizationSecondary 
          : this.defaultWorkload.localizationSecondary;
        let limitOnDate = this.workloadUserData && this.workloadUserData[date]?.localizationSecondaryLimit || this.workloadUserData[date]?.localizationSecondaryLimit === 0
          ? this.workloadUserData[date].localizationSecondaryLimit 
          : this.defaultWorkload.localizationSecondary;
        return { limit, limitOnDate };
      } else if (this.workload === "Copywriting | Primary") {
        let limit = this.workloadUserData && this.workloadUserData[date]?.copywritingPrimary || this.workloadUserData[date]?.copywritingPrimary === 0
          ? this.workloadUserData[date].copywritingPrimary 
          : this.defaultWorkload.copywritingPrimary;
        let limitOnDate = this.workloadUserData && this.workloadUserData[date]?.copywritingPrimaryLimit || this.workloadUserData[date]?.copywritingPrimaryLimit === 0
          ? this.workloadUserData[date].copywritingPrimaryLimit 
          : this.defaultWorkload.copywritingPrimary;
        return { limit, limitOnDate };
      } else if (this.workload === "Copywriting | Secondary") {
        let limit = this.workloadUserData && this.workloadUserData[date]?.copywritingSecondary || this.workloadUserData[date]?.copywritingSecondary === 0
          ? this.workloadUserData[date].copywritingSecondary 
          : this.defaultWorkload.copywritingSecondary;
        let limitOnDate = this.workloadUserData && this.workloadUserData[date]?.copywritingSecondaryLimit || this.workloadUserData[date]?.copywritingSecondaryLimit === 0
          ? this.workloadUserData[date].copywritingSecondaryLimit 
          : this.defaultWorkload.copywritingSecondary;
        return { limit, limitOnDate };
      }
    },
    getVisibleCalendarRange() {
      const selected = new Date(this.value || new Date());
      if (isNaN(selected)) {
        throw new Error('Invalid calendar value');
      }
      selected.setDate(1);

      const weekday = selected.getDay();
      const startOfWeek = 1;
      const dayOffset = (weekday - startOfWeek + 7) % 7;
      const startDate = new Date(selected);
      startDate.setDate(selected.getDate() - dayOffset);

      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 41);

      const formatDate = (d) => d.toISOString().substring(0, 10);

      return {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
      };
    },
    async getCalendarData() {
      if (!this.$refs.calendar || !this.$refs.calendar.lastStart) {
        console.warn('Calendar not ready yet');
        return;
      }

      this.showCalendarLoader = true;

      try {
        const { startDate, endDate } = this.getVisibleCalendarRange();

        const response = await getUserWorkloadSettings(this.user.user.id);
        const response2 = await getMultipleUsersWorkload({ 
          staffIds: [this.user.user.id], 
          startDate,
          endDate
        });

        this.workloadUserData = response2.data[this.user.user.id];
        this.defaultWorkload = response;
      } catch(error) {
        console.log(error);
      } finally {
        this.showCalendarLoader = false;
      }
    },
    async getCurrentPage(date) {
      const newPage = `${this.$moment(date.start.month + "", "M").format("MMMM")} ${this.$moment(date.start.year + "").format("YYYY")}`;
      if (newPage !== this.currentPage) {
        this.currentPage = newPage;
        await this.getCalendarData();
      }
    },
    markUnavailable(value) {
      this.unavailabilityDates.push(value.date);
      this.limitedCapacityDates = [];
      this.note =
        this.limitedCapacityNote =
        this.localization =
        this.editing =
          null;
      this.unavailabilityRecord = true;
    },
    markAvailability(value) {
      this.unavailabilityDates.push(value.date);
      this.editMode = true;
      this.unavailabilityRecord = true;
    },
    handleUnavailabilityRecord() {
      setTimeout(() => {
        this.creationProcessing = true;
        const data = {
          endDate:
            this.tab == 0
              ? this.unavailabilityDates[1]
                ? `${this.unavailabilityDates[1]}T12:00:00`
                : `${this.unavailabilityDates[0]}T12:00:00`
              : this.limitedCapacityDates[1]
              ? `${this.limitedCapacityDates[1]}T12:00:00`
              : `${this.limitedCapacityDates[0]}T12:00:00`,
          note: this.tab == 0 ? this.note : this.limitedCapacityNote,
          personName: {
            firstName: this.user.user.firstName,
            lastName: this.user.user.lastName,
          },
          startDate:
            this.tab == 0
              ? `${this.unavailabilityDates[0]}T12:00:00`
              : `${this.limitedCapacityDates[0]}T12:00:00`,
        };
        if (this.editMode) Object.assign(data, { id: this.workloadUserData[this.unavailabilityDates[0]]?.availabilityId });

        processEvent({ data, staffId: this.user.user.id, mode: this.editMode ? "delete" : null }).then((resp) => {
          this.creationProcessing = false;
          if (resp?.status === 200) {
            if (this.tab == 0)
              publishMessage({
                user: this.user.adminDetails.slackIdPm
                  ? `<@${this.user.adminDetails.slackIdPm}>`
                  : `*${this.user.user.firstName} ${this.user.user.lastName}*`,
                mode: "availability",
                data: {
                  from: this.unavailabilityDates[0],
                  to: this.unavailabilityDates[1],
                  note: this.note,
                },
              });
            this.getCalendarData();
            this.handleUnavailabilityRecordClosing();
          }
          this.handleFlash({
            response: resp,
            show: true,
          });
        });
      }, 100);
    },
    handleUnavailabilityRecordClosing() {
      this.unavailabilityRecord =
        this.editMode =
        this.creationProcessing =
          false;
      this.unavailabilityDates = [];
      this.limitedCapacityDates = [];
    },
    editUnavailabilityRecord(record) {
      this.editMode = true;
      if (record.event.localization || record.event.editing) {
        this.tab = 1;
        record.event.start === record.event.end
          ? this.limitedCapacityDates.push(record.event.start)
          : this.limitedCapacityDates.push(
              record.event.start,
              record.event.end
            );
        this.limitedCapacityNote = record.event.note;
        this.localization = record.event.localization;
        this.editing = record.event.editing;
      } else {
        this.tab = 0;
        record.event.start === record.event.end
          ? this.unavailabilityDates.push(record.event.start)
          : this.unavailabilityDates.push(record.event.start, record.event.end);
        this.note = record.event.note;
      }
      this.recordID = record.event.id;
      this.unavailabilityRecord = true;
    },
    deleteUnavailabilityRecord() {
      this.deletionProcessing = true;
      deleteEvent(this.recordID).then((resp) => {
        this.deletionProcessing = false;
        this.getCalendarData();
        this.handleUnavailabilityRecordClosing();
        this.handleFlash({ response: resp, show: true });
      });
    },
    handleDefaultCapacity() {
      this.localizationDefaultCapacity =
        this.user.adminDetails.defaultDailyLocalization;
      this.editingDefaultCapacity = this.user.adminDetails.defaultDailyEditing;
      this.defaultCapacity = true;
    },
    openDefaultCapacityFromRouteQuery() {
      if (this.$route?.query?.openDefaultCapacity === "true") {
        this.defaultCapacity = true;
        const query = { ...this.$route.query };
        delete query.openDefaultCapacity;
        this.$router.replace({ query }).catch(() => {});
      }
    },
    handleDefaultCapacityUpdate() {
      if (this.$refs.defaultCapacityForm.validate()) {
        this.confirmationProcessing = true;
        const data = {
          defaultDailyLocalization: Number(this.localizationDefaultCapacity),
          defaultDailyEditing: Number(this.editingDefaultCapacity),
        };
        processUser({
          data,
          id: this.user.user.id,
          defaultCapacity: true,
        }).then((resp) => {
          if (resp?.status === 200) {
            getAllStaff({ id: this.user.user.id }).then((resp) => {
              this.setUser(resp);
              publishMessage({
                user: this.user.adminDetails.slackIdFc
                  ? `<@${this.user.adminDetails.slackIdFc}>`
                  : `*${this.user.user.firstName} ${this.user.user.lastName}*`,
                mode: "defaultCapacity",
                data: {
                  locDefault: this.localizationDefaultCapacity,
                  editDefault: this.editingDefaultCapacity,
                },
              });
            });
            this.confirmationProcessing = false;
            this.defaultCapacity = false;
            this.handleFlash({ response: resp, show: true });
          }
        });
      }
    },
    async saveDefaultCapacity() {
      try {
        this.updatingProcessing = true;
        const response = await updateDefaultWorkload({
          staffId: this.user.user.id,
          localizationPrimary: +this.defaultWorkload.localizationPrimary,
          localizationSecondary: +this.defaultWorkload.localizationSecondary,
          copywritingPrimary: +this.defaultWorkload.copywritingPrimary,
          copywritingSecondary: +this.defaultWorkload.copywritingSecondary,
          workingOnWeekend: this.defaultWorkload.workingOnWeekend,
        });
        await this.getCalendarData();
        this.handleFlash({ response: response, show: true });
        this.defaultCapacity = false;
        this.updatingProcessing = false;
      } catch (error) {
        console.log(error);
        this.handleFlash({ response: error, show: true });
        this.defaultCapacity = false;
        this.updatingProcessing = false;
      }
    }
  },
  watch: {
    "$route.query.openDefaultCapacity"(value) {
      if (value === "true") {
        this.openDefaultCapacityFromRouteQuery();
      }
    },
  },
  computed: {
    ...mapGetters("auth", ["user", "isAdmin", "isManager"]),
    localizationPrimary: {
      get() {
        return this.defaultWorkload.localizationPrimary;
      },
      set(value) {
        let sanitized = value.toString().replace(/\D/g, '').slice(0, 5);
        this.defaultWorkload.localizationPrimary = sanitized ? Number(sanitized) : null;
      },
    },
    localizationSecondary: {
      get() {
        return this.defaultWorkload.localizationSecondary;
      },
      set(value) {
        let sanitized = value.toString().replace(/\D/g, '').slice(0, 5);
        this.defaultWorkload.localizationSecondary = sanitized ? Number(sanitized) : null;
      },
    },
    copywritingPrimary: {
      get() {
        return this.defaultWorkload.copywritingPrimary;
      },
      set(value) {
        let sanitized = value.toString().replace(/\D/g, '').slice(0, 5);
        this.defaultWorkload.copywritingPrimary = sanitized ? Number(sanitized) : null;
      },
    },
    copywritingSecondary: {
      get() {
        return this.defaultWorkload.copywritingSecondary;
      },
      set(value) {
        let sanitized = value.toString().replace(/\D/g, '').slice(0, 5);
        this.defaultWorkload.copywritingSecondary = sanitized ? Number(sanitized) : null;
      },
    },
    workloadOptionsList() {
      return this.workloadOptions.map((option) => option.title);
    },
    unavailabilityDatesRange: {
      get: function () {
        return this.unavailabilityDates
          .map((date) => this.formatPickerDate(date))
          .join(" - ");
      },
      set: function (newVal) {
        return (
          this.formatPickerDate(newVal)
            ? [...this.formatPickerDate(newVal)]
            : []
        ).join(" - ");
      },
    },
    isFCStaff() {
      // return (
      //   !!this.user["LastDeliveryDate-Editor"] ||
      //   !!this.user["LastDeliveryDate-Writer"]
      // );
      return !this.clientView; // needs Staff - Project relation
    },
    formMode() {
      return this.isAdmin || this.isManager || this.isFCStaff
        ? ["Unavailability Record", "Limited Capacity"]
        : ["Unavailability Record"];
    },
    unavailabilityDatesValidation() {
      return this.unavailabilityDates[1]
        ? this.$moment(this.unavailabilityDates[0]).isSameOrBefore(
            this.$moment(this.unavailabilityDates[1])
          )
        : true;
    },
    limitedCapacityDatesRange: {
      get: function () {
        return this.limitedCapacityDates
          .map((date) => this.formatPickerDate(date))
          .join(" - ");
      },
      set: function (newVal) {
        return (
          this.formatPickerDate(newVal)
            ? [...this.formatPickerDate(newVal)]
            : []
        ).join(" - ");
      },
    },
    limitedCapacityDateValidation() {
      return this.limitedCapacityDates[1]
        ? this.$moment(this.limitedCapacityDates[0]).isSameOrBefore(
            this.$moment(this.limitedCapacityDates[1])
          )
        : true;
    },
    suggestedNotes() {
      let notes = ["Time off", "At capacity (can't accept new tasks)"];
      if (this.isManager || this.isAdmin)
        notes.push("Sick/personal day", "National holiday");
      return notes;
    },
    btnTxt() {
      return this.editMode ? "Update" : "Save";
    },
    isUnavailabilitySaveDisabled() {
      if (this.editMode) return false;

      if (typeof this.note === "string") {
        return !this.note.trim();
      }

      return !this.note;
    },
  },
  mixins: [timeUnits],
  mounted() {
    this.openDefaultCapacityFromRouteQuery();
  },
};
</script>

<style lang="scss" scoped>
.calendar {
  max-width: 1400px;
  &.form-view {
    height: 708px;
  }
}
::v-deep {
  .v-alert__wrapper {
    justify-content: center;
  }
  .v-alert__content {
    flex: unset;
  }
  .v-event-summary {
    color: #000 !important;
  }
}

.control {
  width: 100%;
  display:flex;
  padding: 12px 30px;
  &__left {
    width: 70%;
    display: flex;
    align-items: center;
    &--month {
      display: flex;
      align-items: center;
      .current-page {
        font-family: Roboto;
        font-weight: 500;
        font-size: 16px;
        line-height: 16px;
        letter-spacing: 0px;
        margin-bottom: 0;
      }
      .control-icon {
        font-size: 24px;
      }
    }
    &--workload {
      padding: 0 10px;
      border-right: 1px solid #C8C8C8;
      border-left: 1px solid #C8C8C8;
      position: relative;
      & label {
        position: absolute;
        top: -2px;
        left: 20px;
        font-size: 10px;
        color: #fff;
      }
      &--select {
        width: 253px;
        height: 32px;
        padding-top: 10px; 
        padding-right: 10px;
        padding-left: 10px;
        border-radius: 6px;
        background-color: #525F7A;
        color: #FFFFFF;
        box-shadow: 0px 2px 4px 0px #00000026;
        font-family: Roboto;
        font-weight: 400;
        font-size: 12px;
        line-height: 10px;
        letter-spacing: 0%;
        cursor: pointer;
        outline: none;
        overflow: hidden;
      }
    }
    &--jumpTo {
      display: flex;
      align-items: center;
      &--text {
        font-family: 'Roboto';
        font-weight: 500;
        color: #525F7A;
        font-size: 16px;
        line-height: 16px;
        letter-spacing: 0px;
        margin: 0 10px;
      }
      &--btn {
        width: 100px;
        height: 32px;
        padding: 4px 3px 2.5px 3px;
        border-radius: 8px;
        color: #525F7A;
        border: 2px solid #525F7A;
        font-family: 'Roboto';
        font-weight: 500;
        font-size: 14px;
        line-height: 16px;
        letter-spacing: 0px;
        text-align: center;
        cursor: pointer;
        outline: none;
        transition: all 0.2s ease-in-out;
        &:hover {
          background-color: #525F7A;
          color: #fff;
        }
      }
    }
  }
  &__right {
    width: 30%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    &--btn {
      width: 166px;
      height: 32px;
      gap: 2;
      border-radius: 8px;
      padding: 4px 3px 2.5px 3px;
      background-color: #0266E3;
      color: #fff;
      font-family: 'Roboto';
      font-weight: 500;
      font-size: 14px;
      line-height: 16px;
      letter-spacing: 0px;
      margin-right: 8px;
      cursor: pointer;
      transition: all 0.2s ease-in-out;
      outline: none;
      &:hover {
        background-color: #fff;
        color: #0266E3;
        border: 2px solid #0266E3;
      }
    }
  }
}

.default-capacity {
  width: 100%;
  background-color: #fff;
  border-radius: 10px;
  padding-top: 15px;
  padding-right: 20px;
  padding-bottom: 30px;
  padding-left: 20px;
}

.custom-day {
  &__empty {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #D6E6FF;
    margin: auto;
    cursor: pointer;
  }
  &__checkmark {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #0266E3;
    margin: auto;
    font-size: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }
  &__exclamation {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #FF7676;
    margin: auto;
    font-size: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #fff;
    cursor: pointer;
  }
  &__close {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #D5D5D5;
    margin: auto;
    font-size: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }
  &__dot {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #D6E6FF;
    margin: auto;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    &--inner {
      background-color: #0266E3;
      min-width: 5px;
      min-height: 5px;
      border-radius: 50%;
    }
  }
}
</style>
